import React, { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../config/api';

const FullscreenChat: React.FC = () => {
  const { user, token } = useAuth();
  const converseRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user || !token) {
      console.log('⏳ Waiting for authentication...');
      return;
    }

    console.log('🔧 Initializing fullscreen Converse.js...', { 
      username: user.username, 
      hasToken: !!token 
    });

    initializeConverse();
  }, [user, token]);

  const initializeConverse = async () => {
    console.log('📱 Starting fullscreen Converse.js initialization...');

    // Load Converse.js dynamically if not already loaded
    if (!window.converse) {
      console.log('📥 Loading Converse.js from CDN...');
      const script = document.createElement('script');
      script.src = 'https://cdn.conversejs.org/12.0.0/dist/converse.min.js';
      script.async = true;
      await new Promise((resolve, reject) => {
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      console.log('✅ Converse.js loaded');
    }

    const userJid = `${user.username}@chat.local`;
    console.log('👤 User JID:', userJid);
    console.log('🔐 Token for XMPP request:', token.substring(0, 10) + '...');
    
    // Get XMPP credentials from Django backend
    let xmppCreds = null;
    try {
      console.log('🔐 Requesting XMPP credentials from Django...');
      const response = await apiCall(ENDPOINTS.XMPP.PREBIND, {
        method: 'POST',
      }, token);
      
      if (!response.ok) {
        console.error('❌ XMPP credentials request failed:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error details:', errorText);
      } else {
        xmppCreds = await response.json();
        console.log('✅ XMPP credentials received:', { 
          jid: xmppCreds.jid
        });
      }
    } catch (error) {
      console.error('❌ XMPP credentials request error:', error);
    }

    const config = {
      // Fullscreen mode configuration
      view_mode: 'fullscreen',
      auto_focus: true,

      // Connection to our Prosody server
      // DEVELOPMENT: Use HTTP BOSH (port 5280) - server considers localhost connections secure
      // PRODUCTION: Change to https://yourdomain.com:5281/http-bind with valid SSL certificate
      bosh_service_url: xmppCreds ? xmppCreds.bosh_url : 'http://localhost:5280/http-bind', // HTTP BOSH for development
      // websocket_url: 'wss://localhost:5281/xmpp-websocket', // WebSocket optional, requires valid SSL cert
      
      // Skip auto-discovery
      discover_connection_methods: false,

      // Authentication using credentials from Django backend
      authentication: 'login',
      auto_login: true,
      jid: xmppCreds ? xmppCreds.jid : userJid,
      password: xmppCreds ? xmppCreds.password : token,

      // Domain settings
      default_domain: 'chat.local',
      domain_placeholder: 'chat.local',

      // UI settings
      show_desktop_notifications: true,
      notification_delay: 3000,
      show_send_button: true,
      
      // MESSAGE PERSISTENCE
      message_archiving: 'always',
      message_archiving_timeout: 20,
      archived_messages_page_size: 50,
      mam_request_all_pages: true,
      clear_cache_on_logout: false,

      // File sharing
      allow_file_upload: true,
      show_client_info: false,

      // Fullscreen features
      show_controlbox_by_default: true,
      locked_domain: 'chat.local',
      
      // Connection settings
      auto_reconnect: true,
      enable_smacks: true,
      message_carbons: true,

      // Theme
      theme: 'concord',

      // Event handlers
      callback: (converse: any) => {
        console.log('🎯 Converse.js callback invoked');

        converse.listen.on('connected', async () => {
          console.log('✅ Fullscreen chat connected');
        });

        converse.listen.on('initialized', () => {
          console.log('✅ Fullscreen chat initialized');
        });

        converse.listen.on('disconnected', () => {
          console.log('❌ Fullscreen chat disconnected');
        });

        converse.listen.on('reconnecting', () => {
          console.log('🔄 Fullscreen chat reconnecting...');
        });
      }
    };

    console.log('🚀 Initializing Converse with fullscreen config:', { ...config, password: '[REDACTED]' });

    try {
      converseRef.current = await window.converse.initialize(config);
      console.log('✅ Fullscreen Converse.js initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize fullscreen Converse.js:', error);
    }
  };

  return (
    <div className="h-screen w-full flex flex-col">
      <div className="bg-base-200 p-4 border-b border-base-300">
        <h1 className="text-2xl font-bold">Templar Edge Chat</h1>
        <p className="text-sm text-base-content/60">
          Logged in as: {user?.first_name && user?.last_name 
            ? `${user.first_name} ${user.last_name}` 
            : user?.username || 'Loading...'}
        </p>
      </div>
      <div ref={containerRef} id="conversejs-fullscreen" className="flex-1 w-full">
        {/* Converse.js will render here in fullscreen mode */}
      </div>
    </div>
  );
};

export default FullscreenChat;
